import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Radio, X } from "lucide-react";
import api from "../lib/api";

/**
 * LiveNowToast — polls /api/live/active every 30s. When a newly-started
 * stream belongs to a creator I follow, pops a bottom-left toast with a
 * "Join" CTA. Only shows one toast at a time; if multiple new streams appear
 * simultaneously, cycles through them 8s apart.
 *
 * Anonymous / non-following users see nothing (endpoint refuses without auth).
 * Mobile hidden — this is a desktop side-toast (`md+`).
 */
export default function LiveNowToast() {
  const [toast, setToast] = useState(null); // { stream } or null
  const seenRef = useRef(new Set());        // stream ids we've already surfaced
  const followingRef = useRef(new Set());   // user ids I follow
  const queueRef = useRef([]);              // pending streams to surface

  useEffect(() => {
    let cancelled = false;
    // 1. Fetch my following ids once (cache 5 minutes)
    api.get("/me/following-ids")
      .then(r => { followingRef.current = new Set(r.data?.ids || []); })
      .catch(() => { /* logged out or endpoint missing → toast is a no-op */ });

    // 2. Poll live/active every 30s.
    const poll = async () => {
      try {
        const r = await api.get("/live/active");
        const rows = r.data || [];
        // First pass: prime seen ids so we don't spam on initial load
        if (seenRef.current.size === 0 && rows.length) {
          rows.forEach(s => seenRef.current.add(s.id));
          return;
        }
        // Any NEW stream from a followed creator?
        rows.forEach(s => {
          if (seenRef.current.has(s.id)) return;
          seenRef.current.add(s.id);
          const hostId = s.user_id || s.user?.id;
          if (!hostId || !followingRef.current.has(hostId)) return;
          queueRef.current.push(s);
        });
        // Trigger surfacing if idle
        if (!cancelled && !toast && queueRef.current.length > 0) {
          setToast({ stream: queueRef.current.shift() });
        }
      } catch { /* ignore transient errors */ }
    };
    poll();
    const id = setInterval(poll, 30_000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-hide + surface next in queue
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => {
      const next = queueRef.current.shift();
      setToast(next ? { stream: next } : null);
    }, 8000);
    return () => clearTimeout(t);
  }, [toast]);

  const dismiss = () => setToast(null);

  return (
    <AnimatePresence>
      {toast && (
        <motion.div
          data-testid="live-now-toast"
          initial={{ opacity: 0, x: -60 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -60 }}
          transition={{ type: "spring", stiffness: 260, damping: 22 }}
          className="hidden md:flex fixed bottom-28 left-6 z-[70] max-w-xs bg-gradient-to-br from-[#0B0F19] to-[#301630] border border-[#FF2A5F]/40 rounded-2xl shadow-[0_10px_40px_rgba(255,42,95,0.25)] p-3 items-start gap-3">
          <div className="relative flex-shrink-0">
            <img src={toast.stream.user?.avatar} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-[#FF2A5F]"/>
            <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-1 py-[1px] rounded-full bg-[#FF2A5F] text-white text-[8px] font-extrabold leading-none">LIVE</span>
            <Radio className="absolute -top-1 -right-1 w-3.5 h-3.5 text-[#FF2A5F] animate-pulse bg-[#0B0F19] rounded-full p-[1px]"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] text-[#FF2A5F] font-extrabold uppercase tracking-wide">Live now</div>
            <div className="text-sm font-bold truncate">@{toast.stream.user?.username || "creator"}</div>
            <div className="text-[11px] text-white/70 truncate">{toast.stream.title || toast.stream.category || "Come join!"}</div>
            <Link to={`/live/${toast.stream.id}`} onClick={dismiss}
              data-testid="live-toast-join"
              className="mt-2 inline-block px-3 py-1 rounded-full bg-[#FF2A5F] text-white text-[11px] font-extrabold active:scale-95 transition">
              Join stream →
            </Link>
          </div>
          <button onClick={dismiss} data-testid="live-toast-close"
            className="p-1 rounded-full hover:bg-white/10 flex-shrink-0" aria-label="Dismiss">
            <X className="w-3.5 h-3.5 text-white/60"/>
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
